/**
 * CR-7475: the length limit on Description of Non-Conformity, shared by the
 * Create NCR and Edit NCR screens so the two cannot disagree again (Edit was
 * capped at 1000, Create not at all).
 *
 * The server is the gate: DEFECT_DESCRIPTION_MAX_LENGTH in
 * backend/quality/serializers/ncr/create_update_checks.py, checked on create
 * and on an edit that changes the text. No `maxLength` on the textarea: the
 * browser would silently drop whatever is pasted past the limit, which is the
 * very thing the CR rules out. The counter turns red instead and Save is
 * refused with a message.
 */

/** Mirrors the backend constant of the same name. */
export const DEFECT_DESCRIPTION_MAX_LENGTH = 5000;

const normalised = (text: string | null | undefined): string =>
  (text ?? '').replace(/\r\n/g, '\n').trim();

/**
 * True when the text is over the limit and would be refused on save.
 *
 * @param value - the text in the box
 * @param original - on Edit, the stored text; an NCR imported with a longer
 *   description saves while that text is left as it is
 */
export function isDefectDescriptionTooLong(
  value: string | null | undefined,
  original?: string | null,
): boolean {
  if ((value ?? '').length <= DEFECT_DESCRIPTION_MAX_LENGTH) return false;
  return original === undefined || normalised(value) !== normalised(original);
}

export interface DefectDescriptionCounterProps {
  value: string | null | undefined;
  /** On Edit, the stored text (see {@link isDefectDescriptionTooLong}). */
  original?: string | null;
}

/**
 * The "n/5000 characters" line under the Description of Non-Conformity box.
 *
 * @param props - {@link DefectDescriptionCounterProps}
 * @returns the counter, red with a reason when the text is over the limit
 */
export function DefectDescriptionCounter({ value, original }: DefectDescriptionCounterProps) {
  const length = (value ?? '').length;
  const over = length > DEFECT_DESCRIPTION_MAX_LENGTH;
  const refused = isDefectDescriptionTooLong(value, original);
  return (
    <p
      className={`text-xs mt-1 ${refused ? 'text-red-600' : 'text-muted-foreground'}`}
      data-testid="defect-description-counter"
    >
      {length}/{DEFECT_DESCRIPTION_MAX_LENGTH} characters
      {refused && ` (please shorten it by ${length - DEFECT_DESCRIPTION_MAX_LENGTH} to save)`}
      {over && !refused && ' (imported text, kept as it is; any change must bring it within the limit)'}
    </p>
  );
}
